import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';
import { getFieldLabel, getFieldLabels } from '../../../lib/utils/csvParser';

const REQUIRED_FIELDS = new Set(['first_name', 'linkedin_url', 'email']);

// Compare only by lowercase + trim; differences in spacing / punctuation
// (E-Mail vs Email, Phone Number vs Phone, etc.) should still surface a hint.
const sameLabel = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

const HeaderCell = ({ canonical, mapping, className }) => {
  const { t } = useTranslation();
  const label = getFieldLabel(canonical) || canonical;
  const matched = mapping?.[canonical];
  const showHint = matched && !sameLabel(matched, label);

  return (
    <th className={cn('text-left py-2.5 pr-3 font-semibold align-top', className)}>
      <div className="flex flex-col gap-0.5">
        <span>{label}</span>
        {showHint && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-muted-foreground"
            title={t('import.preview.matchedFrom', { column: matched })}
          >
            <Icon name="CornerDownLeft" size={10} />
            <span className="truncate max-w-[10rem]">{t('import.preview.fromColumn', { column: matched })}</span>
          </span>
        )}
      </div>
    </th>
  );
};

const ColumnMapping = ({ mapping = {}, detectedHeaders = [], headerErrors = [] }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(headerErrors.length > 0);
  const fieldLabels = getFieldLabels();

  const allCanonical = Object.keys(fieldLabels);
  const matchedCount = allCanonical.filter((c) => mapping[c]).length;
  const mappedRawHeaders = new Set(Object.values(mapping));
  const ignoredHeaders = detectedHeaders.filter((h) => !mappedRawHeaders.has(h));

  return (
    <div
      className={cn(
        'rounded-md border mb-3',
        headerErrors.length > 0 ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-muted/40',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm"
      >
        <span className="flex items-center gap-2">
          <Icon
            name={headerErrors.length > 0 ? 'AlertCircle' : 'Columns'}
            size={16}
            className={headerErrors.length > 0 ? 'text-destructive' : 'text-muted-foreground'}
          />
          <span className="font-medium text-foreground">
            {headerErrors.length > 0 ? t('import.preview.columnsMissing') : t('import.preview.columnsDetected')}
          </span>
          <span className="text-muted-foreground">
            {t('import.preview.matchedCount', { matched: matchedCount, total: allCanonical.length })}
          </span>
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 text-sm border-t border-border/60">
          {headerErrors.length > 0 && (
            <div className="mb-3 space-y-1.5">
              {headerErrors.map((err) => (
                <div key={err.canonical} className="text-destructive text-xs">
                  <strong>{err.field}:</strong> {err.message}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
            {allCanonical.map((canonical) => {
              const matched = mapping[canonical];
              const isRequired = REQUIRED_FIELDS.has(canonical);
              return (
                <div key={canonical} className="flex items-center gap-2 text-xs">
                  <Icon
                    name={matched ? 'CheckCircle2' : isRequired ? 'XCircle' : 'MinusCircle'}
                    size={14}
                    className={
                      matched
                        ? 'text-success flex-shrink-0'
                        : isRequired
                        ? 'text-destructive flex-shrink-0'
                        : 'text-muted-foreground flex-shrink-0'
                    }
                  />
                  <span className="font-medium text-foreground">
                    {fieldLabels[canonical]}
                    {isRequired && <span className="text-destructive ml-0.5">*</span>}
                  </span>
                  <span className="text-muted-foreground">→</span>
                  {matched ? (
                    <code className="text-foreground bg-card border border-border rounded px-1.5 py-0.5">
                      {matched}
                    </code>
                  ) : (
                    <span className={isRequired ? 'text-destructive italic' : 'text-muted-foreground italic'}>
                      {t('import.preview.notFoundInCsv')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {ignoredHeaders.length > 0 && (
            <div className="mt-3 pt-2 border-t border-border/60 text-xs text-muted-foreground">
              <strong>{t('import.preview.ignoredColumns')}</strong>{' '}
              {ignoredHeaders.map((h, i) => (
                <React.Fragment key={h}>
                  <code className="bg-card border border-border rounded px-1 py-0.5">{h}</code>
                  {i < ignoredHeaders.length - 1 ? ' ' : ''}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon, label, value, tone = 'muted' }) => {
  const toneClass = {
    muted: 'bg-muted/60 text-foreground',
    success: 'bg-success/10 text-success',
    destructive: 'bg-destructive/10 text-destructive',
    warning: 'bg-warning/10 text-warning',
    accent: 'bg-accent/10 text-accent',
  }[tone];

  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium', toneClass)}>
      <Icon name={icon} size={14} />
      {value} {label}
    </span>
  );
};

const Cell = ({ value, className, maxWidthClass = 'max-w-[12rem]' }) => {
  const { t } = useTranslation();
  return (
    <td className={cn('py-2 pr-3 align-top', className)} title={value || undefined}>
      {value ? (
        <span className={cn('block truncate', maxWidthClass)}>{value}</span>
      ) : (
        <span className="text-muted-foreground">{t('common.emDash')}</span>
      )}
    </td>
  );
};

const FixedHint = ({ original }) => {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-warning truncate"
      title={t('import.preview.willNormalizeFrom', { original })}
    >
      <Icon name="Wand2" size={12} />
      <span className="truncate">{t('import.preview.was', { original })}</span>
    </span>
  );
};

const PreviewRow = ({ row, willUpdate = false }) => {
  const { t } = useTranslation();
  const { rowNum, status, data, originalLinkedinUrl, originalEmail = '', originalPhone = '', errors } = row;
  const isInvalid = status === 'invalid';

  const normalizedUrl = data.linkedin_url || '';
  const urlChanged = originalLinkedinUrl && normalizedUrl && originalLinkedinUrl !== normalizedUrl;
  const displayUrl = normalizedUrl || originalLinkedinUrl;

  const normalizedEmail = data.email || '';
  const emailChanged = originalEmail && normalizedEmail && originalEmail !== normalizedEmail;
  const displayEmail = normalizedEmail || originalEmail;

  const normalizedPhone = data.phone || '';
  const phoneChanged = originalPhone && normalizedPhone && originalPhone !== normalizedPhone;
  const displayPhone = normalizedPhone || originalPhone;

  return (
    <>
      <tr
        className={cn(
          'border-b border-border/50',
          isInvalid && 'bg-destructive/5',
          !isInvalid && willUpdate && 'bg-accent/5',
        )}
      >
        <td className="py-2 px-3 text-muted-foreground align-top font-data text-xs">{rowNum}</td>
        <td className="py-2 pr-3 align-top">
          <Icon
            name={isInvalid ? 'XCircle' : willUpdate ? 'RefreshCw' : 'CheckCircle2'}
            size={16}
            className={
              isInvalid ? 'text-destructive' : willUpdate ? 'text-accent' : 'text-success'
            }
          />
        </td>
        <td className="py-2 pr-3 align-top">
          <div className="flex flex-col gap-0.5 max-w-[14rem]">
            <span className="block truncate text-foreground" title={data.first_name || undefined}>
              {data.first_name || <span className="text-muted-foreground">{t('common.emDash')}</span>}
            </span>
            {!isInvalid && willUpdate && (
              <span
                className="inline-flex items-center gap-1 text-xs text-accent truncate"
                title={t('import.preview.willUpdateTooltip')}
              >
                <Icon name="RefreshCw" size={12} />
                <span className="truncate">{t('import.preview.willUpdateExisting')}</span>
              </span>
            )}
          </div>
        </td>
        <Cell value={data.last_name} maxWidthClass="max-w-[14rem]" />
        <td className="py-2 pr-3 align-top">
          {displayUrl ? (
            <div className="flex flex-col gap-0.5 max-w-[18rem]">
              <span className="truncate text-foreground" title={displayUrl}>{displayUrl}</span>
              {urlChanged && <FixedHint original={originalLinkedinUrl} />}
            </div>
          ) : (
            <span className="text-muted-foreground">{t('common.emDash')}</span>
          )}
        </td>
        <Cell value={data.current_company} />
        <Cell value={data.current_position} />
        <Cell value={data.location} />
        <td className="py-2 pr-3 align-top">
          {displayEmail ? (
            <div className="flex flex-col gap-0.5 max-w-[14rem]">
              <span className="truncate text-foreground" title={displayEmail}>{displayEmail}</span>
              {emailChanged && <FixedHint original={originalEmail} />}
            </div>
          ) : (
            <span className="text-muted-foreground">{t('common.emDash')}</span>
          )}
        </td>
        <td className="py-2 pr-3 align-top">
          {displayPhone ? (
            <div className="flex flex-col gap-0.5 max-w-[12rem]">
              <span className="truncate text-foreground font-data" title={displayPhone}>{displayPhone}</span>
              {phoneChanged && <FixedHint original={originalPhone} />}
            </div>
          ) : (
            <span className="text-muted-foreground">{t('common.emDash')}</span>
          )}
        </td>
      </tr>
      {isInvalid && errors.length > 0 && (
        <tr className="bg-destructive/5 border-b border-border/50">
          <td colSpan={10} className="py-2 pr-3 pl-10 text-xs text-destructive">
            <div className="flex flex-col gap-1">
              {errors.map((e, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <Icon name="AlertTriangle" size={12} className="mt-0.5 flex-shrink-0" />
                  <span><strong>{e.field}:</strong> {e.message}</span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const PREVIEW_PAGE_SIZE = 50;

const CSVPreview = ({
  rows = [],
  remainingSlots = Infinity,
  existingUrls = new Set(),
  parseError = null,
  loading = false,
  duplicateCheckLoading = false,
  headerMapping = {},
  headerErrors = [],
  detectedHeaders = [],
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PREVIEW_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(
    safePage * PREVIEW_PAGE_SIZE,
    safePage * PREVIEW_PAGE_SIZE + PREVIEW_PAGE_SIZE,
  );
  const {
    validCount,
    invalidCount,
    normalizedCount,
    updateCount,
    newWillBeImported,
    skippedByLimit,
    duplicateInFileCount,
  } = useMemo(() => {
    const validRows = rows.filter((r) => r.status === 'valid');
    const invalidRows = rows.filter((r) => r.status === 'invalid');
    const normalized = rows.filter((r) => {
      const urlChanged = r.originalLinkedinUrl && r.data.linkedin_url && r.originalLinkedinUrl !== r.data.linkedin_url;
      const emailChanged = r.originalEmail && r.data.email && r.originalEmail !== r.data.email;
      const phoneChanged = r.originalPhone && r.data.phone && r.originalPhone !== r.data.phone;
      return urlChanged || emailChanged || phoneChanged;
    }).length;
    const updates = validRows.filter((r) => existingUrls.has(r.data.linkedin_url)).length;
    const news = validRows.length - updates;
    const newImportable = Math.min(news, Math.max(0, remainingSlots));
    const duplicateInFileCount = invalidRows.filter((r) =>
      r.errors?.some((e) => e.code === 'duplicate_in_file'),
    ).length;
    return {
      validCount: validRows.length,
      invalidCount: invalidRows.length,
      normalizedCount: normalized,
      updateCount: updates,
      newWillBeImported: newImportable,
      skippedByLimit: news - newImportable,
      duplicateInFileCount,
    };
  }, [rows, remainingSlots, existingUrls]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 text-muted-foreground text-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          {t('import.preview.parsing')}
        </div>
      </div>
    );
  }

  if (parseError) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-sm text-destructive">
        <div className="flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">{t('import.preview.couldNotRead')}</p>
            <p className="mt-1 text-destructive/90">{parseError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (rows.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-xl font-semibold text-foreground">{t('import.preview.title')}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t('import.preview.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Stat icon="FileText" label={t('import.preview.statRows')} value={rows.length} tone="muted" />
          <Stat icon="CheckCircle2" label={t('import.preview.statValid')} value={validCount} tone="success" />
          {updateCount > 0 && (
            <Stat
              icon="RefreshCw"
              label={t('import.preview.statWillUpdate')}
              value={updateCount}
              tone="accent"
            />
          )}
          {duplicateInFileCount > 0 && (
            <Stat
              icon="Copy"
              label={duplicateInFileCount === 1 ? t('import.preview.statDuplicate') : t('import.preview.statDuplicates')}
              value={duplicateInFileCount}
              tone="warning"
            />
          )}
          {invalidCount > 0 && (
            <Stat icon="XCircle" label={t('import.preview.statWithErrors')} value={invalidCount} tone="destructive" />
          )}
          {normalizedCount > 0 && (
            <Stat
              icon="Wand2"
              label={t('import.preview.statAutoFixed', { count: normalizedCount })}
              value={normalizedCount}
              tone="warning"
            />
          )}
        </div>
      </div>

      <ColumnMapping
        mapping={headerMapping}
        detectedHeaders={detectedHeaders}
        headerErrors={headerErrors}
      />

      {updateCount > 0 && (
        <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 mb-3 text-sm text-foreground flex items-start gap-2">
          <Icon name="RefreshCw" size={16} className="text-accent flex-shrink-0 mt-0.5" />
          <p>{t('import.preview.updateBanner', { count: updateCount })}</p>
        </div>
      )}

      {skippedByLimit > 0 && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 mb-4 text-sm text-foreground flex items-start gap-2">
          <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
          <p>
            {t('import.preview.planLimitBanner', {
              count: skippedByLimit,
              importable: newWillBeImported,
              skipped: skippedByLimit,
            })}
          </p>
        </div>
      )}

      {duplicateCheckLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary" />
          {t('import.preview.checkingDuplicates')}
        </div>
      )}

      <div className="border border-border rounded-md overflow-auto max-h-[32rem] custom-scrollbar">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-muted text-xs uppercase tracking-wide text-muted-foreground shadow-[inset_0_-1px_0_var(--color-border)]">
              <th className="text-left py-2.5 px-3 font-semibold w-12 align-top">{t('import.preview.row')}</th>
              <th className="text-left py-2.5 pr-3 font-semibold w-8 align-top"></th>
              <HeaderCell
                canonical="first_name"
                mapping={headerMapping}
                className="min-w-[8rem] max-w-[14rem]"
              />
              <HeaderCell
                canonical="last_name"
                mapping={headerMapping}
                className="min-w-[8rem] max-w-[14rem]"
              />
              <HeaderCell canonical="linkedin_url" mapping={headerMapping} />
              <HeaderCell canonical="current_company" mapping={headerMapping} />
              <HeaderCell canonical="current_position" mapping={headerMapping} />
              <HeaderCell canonical="location" mapping={headerMapping} />
              <HeaderCell canonical="email" mapping={headerMapping} />
              <HeaderCell canonical="phone" mapping={headerMapping} />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <PreviewRow
                key={row.rowNum}
                row={row}
                willUpdate={existingUrls.has(row.data.linkedin_url)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
        <p className="text-xs text-muted-foreground">
          {t('import.preview.showingRange', {
            count: rows.length,
            from: safePage * PREVIEW_PAGE_SIZE + 1,
            to: Math.min((safePage + 1) * PREVIEW_PAGE_SIZE, rows.length),
            total: rows.length,
          })}
        </p>
        {pageCount > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={safePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="text-xs px-2 py-1 rounded border border-border disabled:opacity-40 hover:bg-muted/60"
            >
              {t('common.previous')}
            </button>
            <span className="text-xs text-muted-foreground">
              {t('import.preview.page', { current: safePage + 1, total: pageCount })}
            </span>
            <button
              type="button"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              className="text-xs px-2 py-1 rounded border border-border disabled:opacity-40 hover:bg-muted/60"
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVPreview;
